import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Copy, X } from 'lucide-react';
import { api } from '../../lib/api';
import { markdownToText } from '../../lib/markdown';
import { Button, ErrorBanner, Textarea, useToast } from '../ui';

/**
 * The assistant panel's cards (part 2). A change the assistant proposes shows as a confirm card with exactly what will
 * change, before and after where there is a before; nothing is written until the person clicks Confirm, and Cancel
 * discards it. A drafted email or message shows with Copy; the assistant never sends it.
 */

export type ActionChange = { label: string; before?: string | null; after: string };
export type RuleCheck = { id: string; name: string; status: 'pass' | 'warn' | 'fail'; text: string; staffOnly?: boolean };
export type AssistantAction = {
  id: number;
  tool: string;
  status: 'proposed' | 'confirming' | 'confirmed' | 'cancelled' | 'failed';
  preview: { title: string; target?: { label: string; link: string } | null; changes: ActionChange[]; note?: string | null; image?: string; checks?: RuleCheck[]; consent?: boolean };
  result: { summary: string; link?: string | null; linkLabel?: string } | null;
  error: string | null;
  chatId: number | null;
  taskId: number | null;
  messageId: number | null;
};
export type MessageDraft = { kind: 'email' | 'message'; to?: string | null; subject?: string | null; body: string };

export function ActionCard({ action, onChange, onNavigate }: { action: AssistantAction; onChange?: (a: AssistantAction) => void; onNavigate?: () => void }) {
  const qc = useQueryClient();
  const [a, setA] = useState(action);
  const [busy, setBusy] = useState<'confirm' | 'cancel' | null>(null);
  const [error, setError] = useState<unknown>(null);
  const hasBefore = a.preview.changes.some((c) => c.before != null);
  const decide = async (what: 'confirm' | 'cancel') => {
    setBusy(what);
    setError(null);
    try {
      const res = await api.post<{ action: AssistantAction }>(`/assistant/actions/${a.id}/${what}`);
      setA(res.action);
      onChange?.(res.action);
      // The portal pages behind the panel show the change at once.
      if (res.action.status === 'confirmed') void qc.invalidateQueries();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className={`ap-action ${a.status}`} aria-label={`Change: ${a.preview.title}`}>
      <div className="ap-action-head">
        <strong>{a.preview.title}</strong>
        {a.preview.target ? (
          <Link to={a.preview.target.link} onClick={onNavigate} className="small">
            {a.preview.target.label}
          </Link>
        ) : null}
      </div>
      <table className="ap-changes">
        {hasBefore ? (
          <thead>
            <tr>
              <th scope="col">
                <span className="sr-only">Field</span>
              </th>
              <th scope="col">Now</th>
              <th scope="col">After</th>
            </tr>
          </thead>
        ) : null}
        <tbody>
          {a.preview.changes.map((c, i) => (
            <tr key={i}>
              <th scope="row">{c.label}</th>
              {hasBefore ? <td className="before">{c.before ?? ''}</td> : null}
              <td className="after">{c.after}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {a.preview.image ? (
        <a href={a.preview.image} target="_blank" rel="noreferrer" className="ap-concept" title="Open the concept full size">
          <img src={a.preview.image} alt={`Concept elevation: ${a.preview.changes.find((c) => c.label === 'Concept')?.after ?? a.preview.title}`} loading="lazy" />
        </a>
      ) : null}
      {a.preview.checks?.length ? <RuleChecks checks={a.preview.checks} /> : null}
      {a.preview.note ? <p className="tiny muted ap-action-note">{a.preview.note}</p> : null}
      <ErrorBanner error={error} />
      {a.status === 'proposed' ? (
        <div className="row wrap">
          {/* A project file card asks before the file goes to the AI model (Trent, 2026-11-02). */}
          <Button variant="primary" size="sm" icon={<Check />} busy={busy === 'confirm'} disabled={!!busy} onClick={() => void decide('confirm')}>
            {a.preview.consent ? 'Send it' : 'Confirm'}
          </Button>
          <Button size="sm" icon={<X />} busy={busy === 'cancel'} disabled={!!busy} onClick={() => void decide('cancel')}>
            {a.preview.consent ? "Don't send it" : 'Cancel'}
          </Button>
          <span className="tiny muted">{a.preview.consent ? 'Nothing from the file goes to the AI model until you choose.' : 'Nothing changes until you confirm.'}</span>
        </div>
      ) : (
        <p className="small ap-action-status" role="status">
          {a.status === 'confirmed' ? (
            <>
              Done. {a.result?.summary}{' '}
              {a.result?.link ? (
                <Link to={a.result.link} onClick={onNavigate}>
                  {a.result.linkLabel ?? 'Open it'}
                </Link>
              ) : null}
            </>
          ) : a.status === 'cancelled' ? (
            a.preview.consent ? 'Not sent. The assistant will not read this file.' : 'Cancelled. Nothing was changed.'
          ) : a.status === 'confirming' ? (
            'Making the change.'
          ) : (
            `This change could not be made, and nothing was changed. ${a.error ?? ''}`
          )}
        </p>
      )}
    </section>
  );
}

const CHECK_WORD: Record<RuleCheck['status'], string> = { pass: 'Passes', warn: 'Check', fail: 'Breaks' };
/** A concept's LFAM rule checks: each rule with what was found, the ones it breaks first. */
function RuleChecks({ checks }: { checks: RuleCheck[] }) {
  const order = { fail: 0, warn: 1, pass: 2 } as const;
  const sorted = [...checks].sort((x, y) => order[x.status] - order[y.status]);
  const fails = checks.filter((c) => c.status === 'fail').length;
  return (
    <details className="ap-checks" open={fails > 0}>
      <summary className="small">{fails ? `LFAM checks: breaks ${fails === 1 ? 'one rule' : `${fails} rules`}` : 'LFAM checks: passes every rule checked'}</summary>
      <ul aria-label="LFAM rule checks">
        {sorted.map((c, i) => (
          <li key={i} className={`ap-check ${c.status}`}>
            <span className="ap-check-word">{CHECK_WORD[c.status]}</span> <b>{c.name}.</b> {c.text}
            {c.staffOnly ? <span className="tiny muted"> Circdal team only.</span> : null}
          </li>
        ))}
      </ul>
    </details>
  );
}

export function MessageDraftCard({ draft }: { draft: MessageDraft }) {
  const toast = useToast();
  const [body, setBody] = useState(draft.body);
  const label = draft.kind === 'email' ? 'Draft email' : 'Draft message';
  return (
    <section className="ap-action draft" aria-label={label}>
      <div className="ap-action-head">
        <strong>{label}</strong>
        {draft.to ? <span className="small muted">To {draft.to}</span> : null}
      </div>
      {draft.subject ? (
        <p className="small" style={{ margin: 0 }}>
          <b>Subject:</b> {draft.subject}
        </p>
      ) : null}
      <Textarea aria-label={`${label} text`} rows={Math.min(12, Math.max(4, body.split('\n').length + 1))} value={body} onChange={(e) => setBody(e.target.value)} />
      <div className="row wrap">
        <Button
          size="sm"
          icon={<Copy />}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(draft.subject ? `Subject: ${draft.subject}\n\n${body}` : body);
              toast('Copied. Send it from your own email.');
            } catch {
              toast('Select the text and copy it.');
            }
          }}
        >
          Copy {draft.kind === 'email' ? 'email' : 'message'}
        </Button>
        <span className="tiny muted">Nothing was sent. The assistant never sends email or messages.</span>
      </div>
    </section>
  );
}

/** Copy for a reply: the words as plain text, with each link written out in full. */
export function CopyReply({ text }: { text: string }) {
  const toast = useToast();
  return (
    <button
      type="button"
      className="btn ghost icon sm ap-copy"
      aria-label="Copy this reply"
      title="Copy"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(markdownToText(text, window.location.origin));
          toast('The reply is copied.');
        } catch {
          toast('Select the reply and copy it.');
        }
      }}
    >
      <Copy />
    </button>
  );
}
